import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { StampSerial } from "@/components/shared";
import { QRBox } from "@/components/QRBox";
import { ArrowLeft, CalendarDays, MapPin, Copy, Check, FileText, Loader2, Search, UserCheck, Pencil, Trash2, X, Download, RotateCcw, Radio } from "lucide-react";
import { toast } from "sonner";

const RSVP_BADGE = {
  attending: { l: "Hadir", c: "bg-positive-bg text-positive border-positive/30" },
  tentative: { l: "Mungkin", c: "bg-brass/10 text-brass border-brass/30" },
  not_attending: { l: "Tak Hadir", c: "bg-brick-bg text-brick border-brick/30" },
};

function Stat({ label, value, tone }) {
  const tones = { good: "text-positive", warn: "text-brass", bad: "text-brick", ink: "text-ink" };
  return (
    <div className="bg-white border border-hairline p-4 rounded-sm">
      <div className="text-xs uppercase tracking-wider text-slate">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tones[tone] || "text-ink"}`}>{value}</div>
    </div>
  );
}

export default function MeetingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState("");
  const [presFilter, setPresFilter] = useState("all"); // all | present | absent
  const [modeFilter, setModeFilter] = useState("all"); // all | physical | online
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/meetings/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Not found")), [id]);
  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    navigator.clipboard.writeText(d.rsvp_link);
    setCopied(true);
    toast.success("Link RSVP disalin / RSVP link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleCheckin = async (p, present, mode) => {
    try {
      await api.post(`/meetings/${id}/checkin`, { member_id: p.member_id, present, mode: mode ?? p.checkin_mode });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/meetings/${id}/attendance/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "attendance_register.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF failed"); }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get(`/meetings/${id}/attendance/excel`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "live_attendance.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Excel failed"); }
  };

  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/meetings/${id}`); toast.success("Mesyuarat dipadam / Meeting deleted"); nav("/meetings"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  if (!d) return <Layout title="Meeting"><div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} />Loading…</div></Layout>;
  const m = d.meeting, s = d.summary, rc = s.responses;
  const att = s.attendance || { total: 0, physical: 0, online: 0, self_checkin: 0, staff_checkin: 0 };
  const pct = m.quorum ? Math.min(100, Math.round((s.checked_in / m.quorum) * 100)) : 0;
  const filtered = d.participants.filter((p) => {
    if (search && !((p.name || "").toLowerCase().includes(search.toLowerCase()) || (p.membership_no || "").includes(search))) return false;
    if (presFilter === "present" && !p.present) return false;
    if (presFilter === "absent" && p.present) return false;
    if (modeFilter !== "all" && p.checkin_mode !== modeFilter) return false;
    return true;
  });

  return (
    <Layout
      title={m.title}
      subtitle={<span className="flex items-center gap-3 flex-wrap text-xs"><span className="flex items-center gap-1"><CalendarDays size={13} className="text-brass" />{m.date} · {m.start_time}–{m.end_time}</span><span className="flex items-center gap-1"><MapPin size={13} className="text-brass" />{m.venue} · {m.modes?.join(" / ")}</span></span>}
      actions={
        <>
          <button onClick={() => nav("/meetings")} className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors"><ArrowLeft size={15} /> <span className="hidden sm:inline">Kembali</span></button>
          <button onClick={() => setEditOpen(true)} data-testid="edit-meeting-btn" className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors"><Pencil size={15} /> <span className="hidden sm:inline">Edit</span></button>
          <button onClick={downloadExcel} data-testid="attendance-excel-btn" className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors"><Download size={15} /> <span className="hidden sm:inline">Excel</span></button>
          <button onClick={downloadPdf} data-testid="attendance-pdf-btn" className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors"><FileText size={15} /> <span className="hidden sm:inline">Daftar PDF</span></button>
          <button onClick={() => setConfirmDel(true)} data-testid="delete-meeting-btn" className="flex items-center gap-1.5 bg-transparent border border-brick/30 text-brick hover:bg-brick-bg px-3 py-2 rounded-sm text-sm transition-colors"><Trash2 size={15} /></button>
        </>
      }
    >
      {/* RSVP + Live Attendance links + QR (independent) */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <QRBox url={d.rsvp_link} label="Survey Kedatangan / RSVP (niat sebelum mesyuarat)" testid="rsvp-qrbox" />
        {d.attendance_link && <QRBox url={d.attendance_link} label="Kehadiran Semasa / Live Check-in (daftar masuk semasa mesyuarat)" testid="checkin-qrbox" />}
      </div>

      {/* RSVP dashboard (intention before event) */}
      <div className="mb-2 text-xs uppercase tracking-wider text-slate flex items-center gap-1.5"><CalendarDays size={13} className="text-brass" /> Survey RSVP / Intention</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Stat label="Hadir / Attending" value={rc.attending} tone="good" />
        <Stat label="Mungkin / Tentative" value={rc.tentative} tone="warn" />
        <Stat label="Tak Hadir / Not" value={rc.not_attending} tone="bad" />
        <Stat label="Belum Jawab / No reply" value={s.eligible - s.total_responses} tone="ink" />
        <Stat label="Proksi / Proxy" value={rc.proxy} tone="ink" />
      </div>

      {/* Live Attendance dashboard (actual check-in during event) — independent from RSVP */}
      <div className="mb-2 text-xs uppercase tracking-wider text-slate flex items-center gap-1.5"><Radio size={13} className="text-brass" /> Kehadiran Semasa / Live Attendance</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Stat label="Jumlah Daftar Masuk / Checked In" value={att.total} tone="good" />
        <Stat label="Fizikal / Physical" value={att.physical} tone="ink" />
        <Stat label="Online" value={att.online} tone="ink" />
        <Stat label="Daftar Sendiri (QR) / Self" value={att.self_checkin} tone="warn" />
      </div>

      <div className="bg-white border border-hairline rounded-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate">Kuorum: <strong className="text-ink font-mono">{s.checked_in} / {m.quorum}</strong> daftar masuk (Fizikal {att.physical} · Online {att.online})</span>
          <span className={`text-sm font-semibold ${s.quorum_met ? "text-positive" : "text-brass"}`} data-testid="quorum-status">{s.quorum_met ? "Kuorum dicapai / Quorum met" : "Belum cukup kuorum"}</span>
        </div>
        <div className="h-2.5 bg-paper rounded-full overflow-hidden">
          <div className={`h-full transition-all ${s.quorum_met ? "bg-positive" : "bg-brass"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Participant check-in list */}
      <div className="bg-white border border-hairline rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-hairline flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-serif text-lg text-ink flex items-center gap-2"><UserCheck size={18} className="text-brass" /> Daftar Kehadiran Semasa / Live Attendance</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={presFilter} onChange={(e) => setPresFilter(e.target.value)} data-testid="filter-presence" className="bg-paper border border-slate-300 rounded-sm px-2 py-1.5 text-sm text-ink outline-none focus:border-brass">
              <option value="all">Semua / All</option>
              <option value="present">Hadir / Checked-in</option>
              <option value="absent">Belum / Not checked-in</option>
            </select>
            <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} data-testid="filter-mode" className="bg-paper border border-slate-300 rounded-sm px-2 py-1.5 text-sm text-ink outline-none focus:border-brass">
              <option value="all">Semua Mod</option>
              <option value="physical">Fizikal</option>
              <option value="online">Online</option>
            </select>
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate/50" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="participant-search" placeholder="Cari / Search" className="bg-paper border border-slate-300 rounded-sm pl-8 pr-3 py-1.5 text-sm outline-none focus:border-brass text-ink w-36 md:w-48" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0"><tr className="bg-paper border-y border-ink/20">
              <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Membership</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Nama / Name</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">RSVP</th>
              <th className="text-center text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Hadir / Present</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Mod</th>
              <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Sumber / Source</th>
              <th className="text-center text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Reset</th>
            </tr></thead>
            <tbody>
              {filtered.map((p) => {
                const b = RSVP_BADGE[p.rsvp];
                return (
                  <tr key={p.member_id} className="ledger-row" data-testid={`participant-${p.membership_no}`}>
                    <td className="py-2 px-4"><StampSerial value={p.membership_no} /></td>
                    <td className="py-2 px-4 text-sm text-ink">{p.name}</td>
                    <td className="py-2 px-4">{b ? <span className={`inline-flex items-center border px-2 py-0.5 rounded-sm text-xs ${b.c}`}>{b.l}{p.rsvp === "attending" && p.rsvp_mode ? ` · ${p.rsvp_mode}` : ""}</span> : <span className="text-xs text-slate/40">—</span>}</td>
                    <td className="py-2 px-4 text-center">
                      <input type="checkbox" checked={p.present} onChange={(e) => toggleCheckin(p, e.target.checked, p.checkin_mode || (p.rsvp_mode) || "physical")} data-testid={`checkin-${p.membership_no}`} className="w-4 h-4 accent-positive cursor-pointer" />
                    </td>
                    <td className="py-2 px-4">
                      {p.present ? (
                        <select value={p.checkin_mode || "physical"} onChange={(e) => toggleCheckin(p, true, e.target.value)} data-testid={`checkin-mode-${p.membership_no}`} className="bg-white border border-slate-300 rounded-sm px-2 py-1 text-xs text-ink outline-none focus:border-brass">
                          <option value="physical">Physical</option><option value="online">Online</option>
                        </select>
                      ) : <span className="text-xs text-slate/40">—</span>}
                    </td>
                    <td className="py-2 px-4">
                      {p.present ? (
                        <span className="text-xs text-slate">{p.checkin_source === "self" ? "Sendiri (QR)" : "Staf"}{p.checked_in_at ? ` · ${p.checked_in_at.slice(11, 16)}` : ""}</span>
                      ) : <span className="text-xs text-slate/40">—</span>}
                    </td>
                    <td className="py-2 px-4 text-center">
                      {p.present ? (
                        <button onClick={() => toggleCheckin(p, false)} data-testid={`reset-checkin-${p.membership_no}`} title="Reset daftar masuk" className="text-slate hover:text-brick transition-colors"><RotateCcw size={15} /></button>
                      ) : <span className="text-xs text-slate/40">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editOpen && <EditMeetingModal meeting={m} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={() => setConfirmDel(false)}>
          <div className="bg-white rounded-md shadow-2xl w-full max-w-sm p-6 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-ink">Padam Mesyuarat?</h3>
            <p className="text-sm text-slate mt-2">Tindakan ini akan memadam mesyuarat "{m.title}" beserta semua RSVP & rekod kehadiran. Tak boleh undo.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setConfirmDel(false)} className="bg-transparent border border-ink/20 text-ink px-4 py-2 rounded-sm text-sm">Batal</button>
              <button onClick={doDelete} disabled={busy} data-testid="confirm-delete-meeting" className="bg-brick text-white hover:bg-brick/90 px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{busy && <Loader2 size={14} className="animate-spin" />}Padam</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function EditMeetingModal({ meeting, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: meeting.title || "", meeting_type: meeting.meeting_type || "AGM", date: meeting.date || "",
    start_time: meeting.start_time || "", end_time: meeting.end_time || "", venue: meeting.venue || "", quorum: meeting.quorum || 0,
  });
  const [modes, setModes] = useState({ Physical: meeting.modes?.includes("Physical"), Online: meeting.modes?.includes("Online") });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Tajuk & tarikh diperlukan");
    setSaving(true);
    try {
      const selModes = Object.entries(modes).filter(([, v]) => v).map(([k]) => k);
      await api.put(`/meetings/${meeting.id}`, { ...form, quorum: parseInt(form.quorum) || 0, modes: selModes });
      toast.success("Mesyuarat dikemas kini");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const field = (k, label, type = "text") => (
    <div><label className="block text-xs font-medium text-slate mb-1">{label}</label>
      <input type={type} value={form[k]} onChange={set(k)} data-testid={`edit-meeting-${k}`} className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-brass text-ink" /></div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline"><h3 className="font-serif text-xl text-ink">Edit Mesyuarat / Edit Meeting</h3><button onClick={onClose} className="text-slate hover:text-ink"><X size={20} /></button></div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">{field("title", "Tajuk / Title *")}</div>
          <div><label className="block text-xs font-medium text-slate mb-1">Jenis / Type</label>
            <select value={form.meeting_type} onChange={set("meeting_type")} className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass">
              <option value="AGM">AGM</option><option value="EGM">EGM</option><option value="Committee">Committee</option>
            </select></div>
          {field("date", "Tarikh / Date *", "date")}
          {field("start_time", "Mula / Start", "time")}
          {field("end_time", "Tamat / End", "time")}
          <div className="md:col-span-2">{field("venue", "Tempat / Venue")}</div>
          {field("quorum", "Kuorum / Quorum", "number")}
          <div><label className="block text-xs font-medium text-slate mb-1">Mod / Mode</label>
            <div className="flex gap-3 mt-2">{["Physical", "Online"].map((mo) => (
              <label key={mo} className="flex items-center gap-1.5 text-sm text-ink cursor-pointer"><input type="checkbox" checked={modes[mo]} onChange={(e) => setModes({ ...modes, [mo]: e.target.checked })} className="accent-brass" /> {mo}</label>
            ))}</div></div>
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3">
          <button onClick={onClose} className="bg-transparent border border-ink/20 text-ink px-4 py-2 rounded-sm text-sm">Batal</button>
          <button onClick={save} disabled={saving} data-testid="edit-meeting-save" className="bg-ink text-white hover:bg-ink-light px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 size={15} className="animate-spin" />}Simpan</button>
        </div>
      </div>
    </div>
  );
}
